import { useState } from 'react'
import Panel from '../../ui/Panel'
import Tabs from '../../ui/Tabs'
import StatusLine from '../../ui/StatusLine'
import Flag from '../../ui/Flag'
import LocationMap from '../../charts/LocationMap'
import { formatAsn } from '../../lib/format'
import { site } from '../../content/site'
import type { IpResponse } from '../../api/client'
import styles from './ConnectionDetails.module.css'

/* Right-aligned link to our sibling IP-lookup service. Opens in a new tab;
   rel keeps referrer and follow so it works as a real back-link. */
const partnerLink = (
    <a className={styles.partner} href={site.partner.href} target="_blank" rel="noopener">
        {site.partner.connectionLabel} ↗
    </a>
)

interface ConnectionDetailsProps {
    self: IpResponse | undefined
    rdns: string | undefined
    rdnsPending: boolean
    loading: boolean
    error: boolean
}

const TABS = [
    { id: 'ip', label: 'IP details' },
    { id: 'location', label: 'Location' },
    { id: 'system', label: 'System' },
]

/** Present, absent, or still loading -- the three states every field here can be
    in, since geoip and rDNS resolve at different times from the address. */
function field(value: string | undefined, loading: boolean): { text: string; muted: boolean } {
    if (loading) return { text: '…', muted: true }
    if (!value) return { text: '—', muted: true }
    return { text: value, muted: false }
}

/**
 * The three tabs the original home page had -- IP details, Location, System --
 * rebuilt in the new style. The Location tab draws the visitor on the same
 * self-hosted world map the leak test uses, which is what let the Google Maps
 * embed (and the API key it shipped in the bundle) be removed entirely.
 */
export default function ConnectionDetails({
    self, rdns, rdnsPending, loading, error,
}: ConnectionDetailsProps) {
    const [tab, setTab] = useState('ip')

    if (error) {
        return (
            <Panel title="Your connection" className={styles.panel}>
                <p className={styles.error}>Could not read your connection details.</p>
            </Panel>
        )
    }

    const ua = self?.userAgent ?? {}
    const str = (v: unknown) => (typeof v === 'string' && v ? v : undefined)
    const header = (k: string) => self?.headers?.[k]

    return (
        <Panel title="Your connection" className={styles.panel} aside={partnerLink}>
            <Tabs items={TABS} active={tab} onChange={setTab} label="Connection details">
                {tab === 'ip' && (
                    <div>
                        <StatusLine label="ip address" mono muted={field(self?.ip, loading).muted}>
                            {field(self?.ip, loading).text}
                        </StatusLine>
                        <StatusLine label="reverse dns" mono muted={rdnsPending || !rdns}>
                            {rdnsPending ? '…' : (rdns ?? 'none')}
                        </StatusLine>
                        <StatusLine label="city" muted={field(self?.cityName, loading).muted}>
                            {field(self?.cityName, loading).text}
                        </StatusLine>
                        <StatusLine label="region" muted={field(self?.region, loading).muted}>
                            {field(self?.region, loading).text}
                        </StatusLine>
                        <StatusLine label="country" muted={loading}>
                            <Flag cc={self?.cc} name={self?.countryName ?? self?.cc} />
                        </StatusLine>
                        <StatusLine label="postal" muted={field(self?.postal, loading).muted}>
                            {field(self?.postal, loading).text}
                        </StatusLine>
                        <StatusLine label="operator" muted={loading}>
                            {self?.asnOrg ?? self?.isp ?? '—'}
                            {' '}
                            <span className={styles.asn}>{formatAsn(self?.asnNumber)}</span>
                        </StatusLine>
                    </div>
                )}

                {tab === 'location' && <LocationMap self={self} />}

                {tab === 'system' && (
                    <div>
                        <StatusLine label="browser" muted={!str(ua.browser)}>
                            {str(ua.browser) ?? '—'} {str(ua.version)}
                        </StatusLine>
                        <StatusLine label="operating system" muted={!str(ua.os)}>
                            {str(ua.os) ?? '—'}
                        </StatusLine>
                        <StatusLine label="platform" muted={!str(ua.platform)}>
                            {str(ua.platform) ?? '—'}
                        </StatusLine>
                        <StatusLine label="languages" muted={!header('accept-language')}>
                            {header('accept-language') ?? '—'}
                        </StatusLine>
                        <StatusLine label="encoding" muted={!header('accept-encoding')}>
                            {header('accept-encoding') ?? '—'}
                        </StatusLine>
                        <StatusLine label="user agent" mono muted={!header('user-agent')}>
                            {header('user-agent') ?? '—'}
                        </StatusLine>
                    </div>
                )}
            </Tabs>
        </Panel>
    )
}
